import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Delete, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, Divider, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ConfirmModal, InfoItem, ViewPage } from '/src/components';
import { useAdvanceStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { getSpeedDialActionProps } from '/src/utils';

/**
 * View Advance Page
 */
export const ViewAdvancePage: React.FC = observer(() => {
  /** Context **/
  const advanceStore = useAdvanceStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Documents
  ]);

  /** Computed **/
  const advance = advanceStore.currentAdvance;

  /** Methods **/

  /**
   * Removes the Advance.
   *
   * @remarks
   * Redirects to the Advances List afterwards.
   */
  const remove = async () => {
    if (!advance) return;

    try {
      await advanceStore.remove(advance._id);

      // Show alert
      toast.success(toastMessages.deleteAdvance.success);

      // Redirect to advances
      navigate('/advances');
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.deleteAdvance.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!advance) return;

    const response = await advanceStore.update({ ...advance, notes });
    if (!response) throw new Error(`Error updating notes for Advance ID ${advance._id}`);

    advanceStore.currentAdvance = response;
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!advance) return;

    const response = await advanceStore.update({ ...advance, files });
    if (!response) throw new Error(`Error updating files for Advance ID ${advance._id}`);

    advanceStore.currentAdvance = response;
  };

  /** Effects **/

  useEffect(() => {
    if (!params.advanceId) return;

    Promise.all([advanceStore.get(params.advanceId)]);
  }, [params.advanceId]);

  /** Render **/
  return (
    <ViewPage
      modelName={advance?.name ?? ''}
      modelType={DataModelType.Advance}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      sidebarFlex="35"
      contentFlex="65"
      noteOptions={{
        mode: CRUDMode.View,
        notes: advance?.notes ?? [],
        onChange: updateNotes,
      }}
      documentOptions={{
        mode: CRUDMode.Edit,
        files: advance?.files ?? [],
        onChange: updateFiles,
      }}
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          icon={<ModeEdit className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Edit, 'Edit Advance')}
          onClick={() => navigate(`/advances/${advance?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          icon={<Delete className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Remove, 'Delete Advance')}
          onClick={() => setShowConfirmDelete(true)}
        />,
      ]}
      modals={
        <ConfirmModal
          content="Are you sure you want to delete this Advance?"
          open={showConfirmDelete}
          setOpen={setShowConfirmDelete}
          onOk={() => remove()}
          label="Confirm Advance Deletion"
          okLabel="Delete"
          cancelLabel="Cancel"
        />
      }
    >
      {advance ? (
        <>
          <InfoItem title="Deal" value={advance?.loan.name} url={`/deals/${advance?.loan._id}`} />
          <Divider />
          <InfoItem title="Amount" value={advance?.amount} bold currency />
          <Divider />
          <InfoItem title="Origination Fee" value={advance?.originationFee} currency />
          <Divider />
          <InfoItem title="Date Requested" value={advance?.dateRequested} date />
          <InfoItem title="Date Fulfilled" value={advance?.dateFulfilled} date />
        </>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
